'use client'

import { useOptionalSidebar } from '@brew/ui/sidebar'
import { motion } from 'motion/react'
import { useTheme } from 'next-themes'
import DitherPaper from '@/components/email-detail/dither-paper'

// Mirrors the constants in components/ui/sidebar.tsx. Kept in sync so the
// background's left edge aligns with the sidebar's right edge instead of
// the viewport, which would otherwise shift the glow's visible center
// off of the SidebarInset's center. Only relevant to the 'fixed' variant —
// the 'contained' variant is scoped to its positioned ancestor instead.
const SIDEBAR_WIDTH_EXPANDED = '12rem'
const SIDEBAR_WIDTH_ICON = '3rem'

function useSidebarLeftInset(): string {
  const sidebar = useOptionalSidebar()
  if (!sidebar || sidebar.isMobile) {
    return '0px'
  }
  return sidebar.state === 'expanded'
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_ICON
}

const backgroundVariants = {
  hidden: {
    opacity: 0,
    '--gradient-size-x': '0%',
    '--gradient-size-y': '0%',
    '--stop-1': '0%',
    '--stop-2': '0%',
    '--stop-3': '0%',
    '--stop-4': '0%',
  },
  visible: {
    opacity: 1,
    '--gradient-size-x': '180%',
    '--gradient-size-y': '220%',
    '--stop-1': '12%',
    '--stop-2': '30%',
    '--stop-3': '45%',
    '--stop-4': '75%',
    transition: {
      duration: 1.2,
      ease: [0.4, 0.0, 0.2, 1] as const,
      opacity: {
        duration: 0.4,
        ease: 'easeOut' as const,
      },
      '--gradient-size-x': {
        duration: 1.0,
        delay: 0.1,
        ease: [0.4, 0.0, 0.2, 1] as const,
      },
      '--gradient-size-y': {
        duration: 1.0,
        delay: 0.1,
        ease: [0.4, 0.0, 0.2, 1] as const,
      },
      '--stop-1': {
        duration: 0.9,
        delay: 0.15,
        ease: 'easeOut' as const,
      },
      '--stop-2': {
        duration: 0.9,
        delay: 0.2,
        ease: 'easeOut' as const,
      },
      '--stop-3': {
        duration: 0.9,
        delay: 0.25,
        ease: 'easeOut' as const,
      },
      '--stop-4': {
        duration: 0.9,
        delay: 0.3,
        ease: 'easeOut' as const,
      },
    },
  },
}

const ditherMaskVariants = {
  hidden: {
    '--mask-size-x': '0%',
    '--mask-size-y': '0%',
  },
  visible: {
    '--mask-size-x': '85%',
    '--mask-size-y': '100%',
    transition: {
      duration: 1.0,
      delay: 0.1,
      ease: [0.4, 0.0, 0.2, 1] as const,
    },
  },
}

type RemixEmailBackgroundProps = {
  /**
   * - 'fixed' (default): full-viewport fixed layer offset past the app
   *   sidebar — the marketing/public presentation where the page owns the
   *   whole viewport.
   * - 'contained': absolutely fills the nearest positioned ancestor. Used
   *   inside the app shell's right content panel so the glow sits behind
   *   (and centered on) the email column and can never paint over the
   *   resizable global chat panel to its left.
   */
  variant?: 'fixed' | 'contained'
}

export function RemixEmailBackground({
  variant = 'fixed',
}: RemixEmailBackgroundProps) {
  const { theme } = useTheme()
  const leftInset = useSidebarLeftInset()
  const isContained = variant === 'contained'

  return (
    <motion.div
      aria-hidden="true"
      className={
        isContained
          ? 'create-page-bg pointer-events-none absolute inset-0 z-0'
          : 'create-page-bg pointer-events-none fixed top-0 right-0 bottom-0 z-0 h-full transition-[left] duration-200 ease-linear'
      }
      initial="hidden"
      animate="visible"
      variants={backgroundVariants}
      style={{
        ...(isContained ? {} : { left: leftInset }),
        // @ts-expect-error - CSS custom properties
        '--gradient-size-x': '180%',
        '--gradient-size-y': '220%',
        '--stop-1': '12%',
        '--stop-2': '30%',
        '--stop-3': '45%',
        '--stop-4': '75%',
      }}
    >
      <motion.div
        className="absolute inset-0 h-full w-full"
        initial="hidden"
        animate="visible"
        variants={ditherMaskVariants}
        style={{
          // The fixed variant spans the viewport from y=0, so the dither
          // is pushed below the sticky app/marketing header; the contained
          // variant's box already starts below the header.
          marginTop: isContained ? '0px' : '80px',
          // @ts-expect-error - CSS custom properties
          '--mask-size-x': '85%',
          '--mask-size-y': '100%',
          WebkitMaskImage:
            'radial-gradient(ellipse var(--mask-size-x) var(--mask-size-y) at 50% 55%, black 30%, transparent 75%)',
          maskImage:
            'radial-gradient(ellipse var(--mask-size-x) var(--mask-size-y) at 50% 55%, black 30%, transparent 75%)',
        }}
      >
        <DitherPaper
          waveSpeed={0.06}
          waveFrequency={5}
          waveAmplitude={0.35}
          waveColor={theme === 'dark' ? [0.5, 0.5, 0.5] : [0.95, 0.5, 0.2]}
          colorNum={theme === 'dark' ? 8 : 5}
          pixelSize={1}
        />
      </motion.div>
    </motion.div>
  )
}
